import classNames from 'classnames';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, PageHeader } from '@/components/ui/page';
import { showDemo } from '@/constants/routes';
import { MOCK_INSTAGRAM_THREADS, type InboxThread } from '@/mocks/inbox';
import type { TelegramChat } from '@/types/api';
import { formatRelative, shownEmail } from '@/utils/format';
import { chatKindLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';

import ChannelNav from './channel-nav';
import ChatPanel from './chat-panel';
import { EmailThreadPanel, emailThreadsOf, isFormThread, type EmailThread, type InboundEmail } from './email-page';
import { InboxThreadPanel } from './inbox';

type Channel = 'telegram' | 'email' | 'instagram';

/** One conversation, on whichever channel it happens: one row per lead and channel. */
type Row =
  | { channel: 'telegram'; key: string; at: string; chat: TelegramChat }
  | { channel: 'email'; key: string; at: string; thread: EmailThread }
  | { channel: 'instagram'; key: string; at: string; thread: InboxThread };

const Icon = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const CHANNELS: Record<Channel, { label: string; icon: ReactNode }> = {
  telegram: {
    label: 'Telegram',
    icon: (
      <Icon>
        <path d="M14 2.5L1.8 7.3l4.4 1.6M14 2.5l-2.2 11-5.6-4.6M14 2.5L6.2 8.9l-.4 4.1 2.3-2.3" />
      </Icon>
    ),
  },
  email: {
    label: 'Email',
    icon: (
      <Icon>
        <rect x="1.8" y="3.3" width="12.4" height="9.4" rx="1.5" />
        <path d="M2.3 4.2L8 8.6l5.7-4.4" />
      </Icon>
    ),
  },
  instagram: {
    label: 'Instagram',
    icon: (
      <Icon>
        <rect x="2" y="2" width="12" height="12" rx="3.5" />
        <circle cx="8" cy="8" r="2.8" />
        <circle cx="11.6" cy="4.4" r="0.5" fill="currentColor" />
      </Icon>
    ),
  },
};

/** Which channel a conversation is on: an icon and its name, so the colour never carries it alone. */
const ChannelMarker = ({ channel }: { channel: Channel }) => (
  <span className="pill inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs text-fg-muted">
    {CHANNELS[channel].icon}
    {CHANNELS[channel].label}
  </span>
);

const lastOf = (t: InboxThread) => t.messages[t.messages.length - 1];

const rowsOf = (chats: TelegramChat[], mails: InboundEmail[], instagram: InboxThread[]): Row[] =>
  [
    ...chats.map((chat): Row => ({ channel: 'telegram', key: `telegram:${chat.chat_id}`, at: chat.last_message_at ?? chat.joined_at, chat })),
    ...emailThreadsOf(mails).map((thread): Row => ({ channel: 'email', key: `email:${thread.key}`, at: thread.last.received_at, thread })),
    ...instagram.map((thread): Row => ({ channel: 'instagram', key: `instagram:${thread.id}`, at: lastOf(thread)?.sentAt ?? '', thread })),
  ].sort((a, b) => b.at.localeCompare(a.at));

/** What a row shows: who, the latest line, and what it needs from the team. */
const describe = (r: Row): { title: string; who: string; preview: string; flags: ReactNode } => {
  if (r.channel === 'telegram') {
    const c = r.chat;
    const unlinked = !c.lead_id && c.kind !== 'private' && !c.left_at;
    return {
      title: c.title,
      who: c.lead_name || shownEmail(c.lead_email) || shownEmail(c.claimed_email) || (c.kind === 'private' ? chatKindLabel(c.kind) : 'Not linked to a lead yet'),
      preview: c.last_message_text ? `${c.last_message_from}: ${c.last_message_text}` : 'No messages yet.',
      flags: unlinked ? <Badge tone="warn">To confirm</Badge> : null,
    };
  }
  if (r.channel === 'email') {
    const t = r.thread;
    return {
      title: t.name,
      who: t.contact,
      preview: t.last.subject || t.last.body.split('\n').find((l) => l.trim()) || '',
      flags: isFormThread(t) ? <Badge tone="info">Contact form</Badge> : null,
    };
  }
  const t = r.thread;
  const last = lastOf(t);
  return {
    title: t.name,
    who: t.contact,
    preview: last ? `${last.from}: ${last.text}` : '',
    flags: (
      <>
        {t.status === 'needs_reply' && <Badge tone="warn">Needs a reply</Badge>}
        {t.status === 'new_lead' && <Badge tone="info">New lead</Badge>}
        <DemoPill title="Made up: Instagram is not connected yet (Q18)">Demo</DemoPill>
      </>
    ),
  };
};

/**
 * Conversations on every channel in one list, the latest first (TG-*, LEAD-6, Q17, Q18).
 * Each row says its channel, and opens that channel's own panel. Telegram and email are
 * real; Instagram is made up until Q18, only locally (showDemo), and each of its rows says so. A lead who writes
 * on two channels has a row on each.
 */
const AllConversationsPage = () => {
  const [params, setParams] = useSearchParams();
  const chats = useApi<TelegramChat[]>('/telegram/chats');
  const mails = useApi<InboundEmail[]>('/inbound-emails', { limit: 300 });
  const [instagram, setInstagram] = useState<InboxThread[]>(showDemo ? MOCK_INSTAGRAM_THREADS : []);

  // ?c=<channel>:<key> opens a conversation; links from elsewhere use ?chat=<telegram chat id>.
  const selected = params.get('c') ?? (params.get('chat') ? `telegram:${params.get('chat')}` : null);
  const select = (key: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.delete('chat');
        if (key) next.set('c', key);
        else next.delete('c');
        return next;
      },
      { replace: true },
    );

  const rows = rowsOf(chats.data ?? [], mails.data ?? [], instagram);
  const open = rows.find((r) => r.key === selected);
  const loading = !chats.data && !mails.data && (chats.loading || mails.loading);
  const failed = [chats.error && !chats.data && 'Telegram', mails.error && !mails.data && 'email'].filter(Boolean);

  return (
    <div className={classNames('transition-[padding]', selected && 'xl:pr-[452px]')}>
      <PageHeader title="Conversations" description="Every conversation with a lead or a client, on every channel, the latest first.">
        <ChannelNav />
      </PageHeader>

      {failed.length > 0 && <p className="mb-3 text-sm text-danger">The {failed.join(' and ')} conversations could not be loaded.</p>}

      {loading ? (
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      ) : rows.length === 0 ? (
        <StateMessage title="No conversations yet">They show up here when a client writes on Telegram, by email or on Instagram.</StateMessage>
      ) : (
        <ul className="overflow-hidden rounded-card bg-surface shadow-e1">
          {rows.map((r) => {
            const d = describe(r);
            // Email and Instagram do not work in this version: faded, with the info mark after the time.
            const off = r.channel === 'email' ? 'mailbox' : r.channel === 'instagram' ? 'instagram' : null;
            const fade = off ? notYet : undefined;
            const time = (r.at || off) && (
              <>
                {r.at && <span className={classNames('text-xs text-fg-faint', fade)}>{formatRelative(r.at)}</span>}
                {off && <NotYetInfo what={off} nested />}
              </>
            );
            return (
              <li key={r.key} className="border-b border-line last:border-0">
                <button
                  type="button"
                  onClick={() => select(r.key)}
                  aria-current={selected === r.key}
                  className={classNames(
                    'flex w-full min-w-0 flex-col gap-1 px-4 py-3 text-left transition-colors md:flex-row md:items-start md:gap-4',
                    selected === r.key ? 'bg-brand/10' : 'hover:bg-surface-hover',
                  )}
                >
                  <div className="flex shrink-0 items-center justify-between gap-2 md:w-32 md:flex-col md:items-start md:justify-start">
                    <span className={fade}>
                      <ChannelMarker channel={r.channel} />
                    </span>
                    {time && <span className="flex items-center gap-1.5 md:hidden">{time}</span>}
                  </div>
                  <div className={classNames('min-w-0 flex-1', fade)}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="min-w-0 truncate text-sm text-fg-strong">{d.title}</span>
                      {d.flags}
                    </div>
                    <p className="truncate text-xs text-fg-muted">{d.who}</p>
                    {d.preview && <p className="mt-0.5 line-clamp-2 break-words text-sm text-fg">{d.preview}</p>}
                  </div>
                  {time && <span className="hidden shrink-0 items-center gap-1.5 md:flex">{time}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {open?.channel === 'telegram' && <ChatPanel id={String(open.chat.chat_id)} onClose={() => select(null)} onChanged={chats.reload} />}
      {open?.channel === 'email' && <EmailThreadPanel thread={open.thread} onClose={() => select(null)} />}
      {open?.channel === 'instagram' && (
        <InboxThreadPanel
          thread={open.thread}
          onClose={() => select(null)}
          onChange={(next) => setInstagram((ts) => ts.map((t) => (t.id === next.id ? next : t)))}
        />
      )}
    </div>
  );
};

export default AllConversationsPage;
